import React, { useState, useEffect } from 'react';
import { Grid, TextField, Typography } from '@material-ui/core';

import EditCard from 'components/EditCard';

import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import 'translations/translations';
import { useTranslation } from 'react-i18next';
import { departmentActions } from 'actions/departmentActions';
import { notificationActions } from 'actions/notificationActions';

const DepartmentCard: React.FC = () => {
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState({ name: '', departmentNumber: '' });

  const department = useSelector(
    (state: ApplicationState) => state.department?.department,
  );

  // track unmount
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  // track department
  useEffect(() => {
    resetInput();
  }, [department]);

  const resetInput = () => {
    if (department) {
      setInput({
        name: department.name,
        departmentNumber: department.departmentNumber
          ? department.departmentNumber.toString()
          : '',
      });
    } else {
      setInput({ name: '', departmentNumber: '' });
    }
  };

  const handleSave = () => {
    if (department) {
      setIsLoading(true);
      dispatch(
        departmentActions.updateDepartmentInfo(
          department.departmentId,
          input.name,
          parseInt(input.departmentNumber),
          (error) => {
            if (isMounted) {
              setIsLoading(false);
              setEditMode(!!error);
            }
            if (error) {
              dispatch(notificationActions.enqueueErrorResponse(error));
            }
          },
        ),
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInput((input) => ({ ...input, [name]: value }));
  };

  const handleEdit = () => {
    resetInput();
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  return (
    <EditCard
      editMode={editMode}
      isLoading={isLoading}
      title={t('department_page.department_information.labels.department')}
      onEdit={handleEdit}
      onCancel={handleCancel}
      onSave={handleSave}
    >
      {editMode ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <TextField
              type="text"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'department_page.department_information.labels.department_name',
              )}
              name="name"
              value={input.name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <TextField
              type="number"
              variant="outlined"
              margin="none"
              fullWidth
              label={t(
                'department_page.department_information.labels.department_number',
              )}
              name="departmentNumber"
              value={input.departmentNumber}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      ) : (
        <>
          <Typography variant="body1">
            <b>
              {t(
                'department_page.department_information.labels.department_name',
              )}
              :
            </b>
            &nbsp;
            {department?.name ?? ''}
          </Typography>
          <Typography variant="body1">
            <b>
              {t(
                'department_page.department_information.labels.department_number',
              )}
              :
            </b>
            &nbsp;
            {department?.departmentNumber ?? ''}
          </Typography>
        </>
      )}
    </EditCard>
  );
};

export default DepartmentCard;
